import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useApiConfiguration } from '@app/hooks/useApiConfiguration';
import { useActiveOrgId } from '@app/hooks/useActiveOrg';
import { useAuthFetch } from '@app/hooks/useAuthFetch';
import { cloneHeadersWithAuth, ApiResponseError } from '@app/utils/api';
import Drawer from '@app/components/Drawer';

type MibUploadItem = {
  path: string;
  vendor?: string;
  moduleName?: string;
  enterpriseNumber?: number;
  inferredVendor?: string;
  checksum?: string;
  status?: string;
  message?: string;
  documentId?: string;
  contentUri?: string;
  previewUri?: string;
};

type MibUploadResult = {
  items?: MibUploadItem[];
};

type MibChunkPreview = {
  document_id?: string;
  document_name?: string;
  document_type?: string;
  vendor?: string;
  chunks?: Array<{ chunk_id: string; text: string; metadata?: Record<string, unknown> }>;
};

type FileSelection = {
  file: File;
  path: string;
  vendorOverride: string;
};

const MibLibraryPanel = () => {
  const configuration = useApiConfiguration();
  const { fetchWithAuthRetry } = useAuthFetch();
  const activeOrgId = useActiveOrgId();
  const basePath = (configuration.basePath ?? '').replace(/\/+$/, '');
  const apiRoot = useMemo(() => (basePath ? basePath.replace(/\/v1$/, '') : ''), [basePath]);

  const endpoint = useMemo(() => {
    if (!activeOrgId) return null;
    const prefix = apiRoot.length === 0 ? '' : apiRoot;
    return `${prefix}/v1/orgs/${activeOrgId}/knowledge/mib-library/modules`;
  }, [activeOrgId, apiRoot]);

  const [selected, setSelected] = useState<FileSelection[]>([]);
  const [results, setResults] = useState<MibUploadItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<MibChunkPreview | null>(null);
  const [selectedChunkIndex, setSelectedChunkIndex] = useState<number>(0);

  const buildAuthHeaders = useCallback(async () => {
    const headersMap = await cloneHeadersWithAuth({ Accept: 'application/json' });
    if (activeOrgId) headersMap['X-Org-Id'] = activeOrgId;
    return new Headers(headersMap);
  }, [activeOrgId]);

  const handleFolderChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    const next = files.map((file) => {
      const path = (file as any).webkitRelativePath?.toString?.() || file.name;
      return { file, path, vendorOverride: '' };
    });
    next.sort((a, b) => a.path.localeCompare(b.path));
    setSelected(next);
    setResults(null);
    setError(null);
  }, []);

  const updateOverride = useCallback((path: string, vendorOverride: string) => {
    setSelected((prev) => prev.map((item) => (item.path === path ? { ...item, vendorOverride } : item)));
  }, []);

  const submit = useCallback(
    async (dryRun: boolean) => {
      if (!endpoint) return;
      if (selected.length === 0) {
        setError('Select a folder with MIB files.');
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const formData = new FormData();
        const manifest = selected
          .filter((item) => item.vendorOverride.trim().length > 0)
          .map((item) => ({ path: item.path, vendor: item.vendorOverride.trim() }));
        if (manifest.length > 0) {
          formData.append('manifest', JSON.stringify(manifest));
        }
        for (const item of selected) {
          // Use the relative path as the multipart filename so server-side overrides can match.
          formData.append('file', item.file, item.path);
        }
        const url = dryRun ? `${endpoint}?dryRun=1` : endpoint;
        const headers = await buildAuthHeaders();
        const response = await fetchWithAuthRetry(url, { method: 'POST', headers, body: formData });
        if (!response.ok) throw new ApiResponseError(response, `MIB upload failed (${response.status})`);
        const payload = (await response.json()) as MibUploadResult;
        setResults(payload.items ?? []);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'MIB upload failed.';
        setError(message);
      } finally {
        setLoading(false);
      }
    },
    [endpoint, selected, buildAuthHeaders, fetchWithAuthRetry]
  );

  const openPreview = useCallback(
    async (vendor: string, moduleName: string) => {
      if (!endpoint) return;
      setPreviewOpen(true);
      setPreviewLoading(true);
      setPreviewError(null);
      setPreviewData(null);
      setSelectedChunkIndex(0);
      try {
        const headers = await buildAuthHeaders();
        const base = endpoint.replace(/\/modules\/?$/, '');
        const url = `${base}/modules/${encodeURIComponent(vendor)}/${encodeURIComponent(moduleName)}/preview`;
        const response = await fetchWithAuthRetry(url, { method: 'GET', headers });
        if (!response.ok) throw new ApiResponseError(response, `Preview fetch failed (${response.status})`);
        const payload = (await response.json()) as MibChunkPreview;
        setPreviewData(payload);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load preview.';
        setPreviewError(message);
      } finally {
        setPreviewLoading(false);
      }
    },
    [endpoint, buildAuthHeaders, fetchWithAuthRetry]
  );

  const closePreview = useCallback(() => {
    setPreviewOpen(false);
    setPreviewLoading(false);
    setPreviewError(null);
    setPreviewData(null);
    setSelectedChunkIndex(0);
  }, []);

  const previewChunks = previewData?.chunks ?? [];
  const selectedChunk = previewChunks[selectedChunkIndex] ?? null;

  return (
    <div className="form-grid form-grid--single">
      <div className="empty-hero" style={{ textAlign: 'left' }}>
        <h3 className="empty-hero__title">MIB library</h3>
        <p className="empty-hero__body">
          Upload a folder of `.mib` files. Vendor is inferred from enterprise OIDs; unknown files default to `common`.
        </p>
      </div>

      <label className="form-field">
        <span className="form-field__label">Folder</span>
        <input
          type="file"
          className="input"
          multiple
          // webkitdirectory is the de-facto standard for folder pickers.
          {...({ webkitdirectory: '' } as any)}
          onChange={handleFolderChange}
        />
        <span className="form-field__hint">{selected.length ? `${selected.length} file(s) selected.` : 'Select a directory.'}</span>
      </label>

      {selected.length > 0 ? (
        <div className="form-field">
          <span className="form-field__label">Vendor overrides (optional)</span>
          <div style={{ maxHeight: 320, overflow: 'auto', border: '1px solid var(--ui-border, #ddd)', borderRadius: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: 8 }}>Path</th>
                  <th style={{ textAlign: 'left', padding: 8 }}>Override vendor</th>
                </tr>
              </thead>
              <tbody>
                {selected.map((item) => (
                  <tr key={item.path}>
                    <td style={{ padding: 8, fontFamily: 'var(--font-mono)' }}>{item.path}</td>
                    <td style={{ padding: 8 }}>
                      <input
                        className="input"
                        value={item.vendorOverride}
                        onChange={(e) => updateOverride(item.path, e.target.value)}
                        placeholder="common / cisco / juniper …"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <span className="form-field__hint">
            Leave blank to use auto-detect. If no enterprise OID match exists, the module will be stored as vendor=`common`.
          </span>
        </div>
      ) : null}

      {error ? <p className="discovery-inline-error">{error}</p> : null}

      <div className="form-actions">
        <button type="button" className="ui-button ui-button--ghost" disabled={loading || !endpoint} onClick={() => submit(true)}>
          {loading ? 'Working…' : 'Analyze (dry run)'}
        </button>
        <button type="button" className="ui-button ui-button--primary" disabled={loading || !endpoint} onClick={() => submit(false)}>
          {loading ? 'Working…' : 'Upload & ingest'}
        </button>
      </div>

      {results ? (
        <div className="form-field">
          <span className="form-field__label">Result</span>
          <div style={{ maxHeight: 360, overflow: 'auto', border: '1px solid var(--ui-border, #ddd)', borderRadius: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: 8 }}>Path</th>
                  <th style={{ textAlign: 'left', padding: 8 }}>Module</th>
                  <th style={{ textAlign: 'left', padding: 8 }}>Vendor</th>
                  <th style={{ textAlign: 'left', padding: 8 }}>Status</th>
                  <th style={{ textAlign: 'left', padding: 8 }}>Message</th>
                  <th style={{ textAlign: 'left', padding: 8 }}>Preview</th>
                </tr>
              </thead>
              <tbody>
                {results.map((item) => (
                  <tr key={`${item.path}-${item.moduleName ?? ''}`}>
                    <td style={{ padding: 8, fontFamily: 'var(--font-mono)' }}>{item.path}</td>
                    <td style={{ padding: 8 }}>{item.moduleName ?? '—'}</td>
                    <td style={{ padding: 8 }}>{item.inferredVendor ?? '—'}</td>
                    <td style={{ padding: 8 }}>{item.status ?? '—'}</td>
                    <td style={{ padding: 8 }}>{item.message ?? '—'}</td>
                    <td style={{ padding: 8 }}>
                      {item.inferredVendor && item.moduleName && item.previewUri ? (
                        <button
                          type="button"
                          className="ui-button ui-button--ghost"
                          onClick={() => openPreview(item.inferredVendor as string, item.moduleName as string)}
                        >
                          Preview chunks
                        </button>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <Drawer open={previewOpen} title="MIB chunk preview" subtitle="Chunks captured just before embedding." onClose={closePreview} width="wide">
        {previewLoading ? (
          <p>Loading preview…</p>
        ) : previewError ? (
          <p className="discovery-inline-error">{previewError}</p>
        ) : !previewData ? (
          <p>No preview loaded.</p>
        ) : (
          <div className="chunk-viewer">
            <div className="chunk-viewer__hierarchy">
              <div className="chunk-viewer__column chunk-viewer__column--chunks">
                <div className="chunk-viewer__column-title">
                  Chunks ({previewChunks.length}) · {previewData.document_name || previewData.document_id || ''}
                </div>
                <div className="chunk-viewer__column-items chunk-viewer__column-items--scroll">
                  {previewChunks.map((chunk, idx) => (
                    <button
                      key={chunk.chunk_id}
                      type="button"
                      className={`chunk-viewer__chunk ${idx === selectedChunkIndex ? 'is-active' : ''}`}
                      onClick={() => setSelectedChunkIndex(idx)}
                    >
                      <span className="chunk-viewer__chunk-title">
                        {(chunk.metadata?.section_title as string) || (chunk.metadata?.sectionTitle as string) || chunk.chunk_id}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="chunk-viewer__detail">
              {selectedChunk ? (
                <>
                  <div className="chunk-viewer__detail-header">
                    <div>
                      <h4>Chunk preview</h4>
                      <p className="chunk-viewer__detail-breadcrumb">{previewData.vendor ? `Vendor: ${previewData.vendor}` : ''}</p>
                    </div>
                  </div>
                  <div className="chunk-viewer__detail-text">
                    {selectedChunk.text ? selectedChunk.text : <span className="chunk-viewer__empty">Chunk text is empty.</span>}
                  </div>
                </>
              ) : (
                <div className="chunk-viewer__empty">Select a chunk to view the content.</div>
              )}
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default MibLibraryPanel;
